import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Eye,
  Luggage,
  Plus,
  Printer,
  Search,
  Tags,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "contexts/PageHeaderContext";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";

type AssignedApplicant = {
  id: string;
  name: string;
  its: string;
  tour: string;
  operator: string;
  arrival: string;
  venue: string;
  property: string;
  roomNo: string;
};

type TagBatch = {
  id: string;
  tour: string;
  arrival: string;
  venue: string;
  property: string;
  applicants: number;
  printed: number;
  generatedAt: string;
  status: "Ready" | "Partially Printed" | "Printed";
  applicantIds?: string[];
};

const assignedApplicants: AssignedApplicant[] = [
  {
    id: "APP-101",
    name: "Ali Hassan Zaidi",
    its: "10223344",
    tour: "Ziyarat Najaf 2026",
    operator: "Faiz-e-Husaini Mumbai",
    arrival: "2026-08-05",
    venue: "Najaf",
    property: "Faiz Hussainia",
    roomNo: "101",
  },
  {
    id: "APP-102",
    name: "Ruqaiyya Ali Zaidi",
    its: "10223345",
    tour: "Ziyarat Najaf 2026",
    operator: "Faiz-e-Husaini Mumbai",
    arrival: "2026-08-05",
    venue: "Najaf",
    property: "Faiz Hussainia",
    roomNo: "101",
  },
  {
    id: "APP-103",
    name: "Hussain Ali Zaidi",
    its: "10223346",
    tour: "Ziyarat Najaf 2026",
    operator: "Faiz-e-Husaini Mumbai",
    arrival: "2026-08-05",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    roomNo: "204",
  },
  {
    id: "APP-104",
    name: "Dr. Raza Hussain Merchant",
    its: "55667788",
    tour: "Miqaat Karbala 2026",
    operator: "Dubai Ziyarat Group",
    arrival: "2026-08-06",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    roomNo: "202",
  },
  {
    id: "APP-105",
    name: "Fatema Merchant",
    its: "55667789",
    tour: "Miqaat Karbala 2026",
    operator: "Dubai Ziyarat Group",
    arrival: "2026-08-06",
    venue: "Karbala",
    property: "Al-Amin Hotel",
    roomNo: "202",
  },
  {
    id: "APP-106",
    name: "Zahra Ali Hussain",
    its: "40112233",
    tour: "External Hotel Test Tour",
    operator: "London Mumineen Group",
    arrival: "2026-08-07",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    roomNo: "310",
  },
];

const initialBatches: TagBatch[] = [
  {
    id: "LT-260801",
    tour: "Ashara Mubarak 1448H",
    arrival: "01 Aug 2026",
    venue: "Karbala",
    property: "Faiz Hussainia",
    applicants: 42,
    printed: 42,
    generatedAt: "01 Aug 2026 · 09:20",
    status: "Printed",
  },
  {
    id: "LT-260802",
    tour: "Najaf Ziyarat Group",
    arrival: "03 Aug 2026",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    applicants: 18,
    printed: 10,
    generatedAt: "03 Aug 2026 · 16:45",
    status: "Partially Printed",
  },
  {
    id: "LT-260803",
    tour: "External Hotel Test Tour",
    arrival: "07 Aug 2026",
    venue: "Najaf",
    property: "Grand Hotel Najaf",
    applicants: 12,
    printed: 0,
    generatedAt: "04 Aug 2026 · 10:15",
    status: "Ready",
  },
];

const displayDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const TagCard = ({ applicant }: { applicant: AssignedApplicant }) => {
  return (
    <div className="w-full min-w-0 overflow-hidden rounded-xl border-2 border-sky-200 bg-white shadow-sm">
      <div className="flex items-center justify-between bg-[#E3F2FD] px-4 py-2">
        <div className="text-xs font-bold text-[#347DB5]">
          FHIMS LUGGAGE TAG
        </div>
        <Luggage className="h-4 w-4 text-[#5B9BD5]" />
      </div>
      <div className="space-y-3 p-3 sm:p-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">
            Applicant
          </div>
          <div className="text-base font-bold text-slate-900">
            {applicant.name}
          </div>
          <div className="text-xs text-slate-500">ITS {applicant.its}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-y border-dashed border-slate-200 py-3 sm:grid-cols-[1fr_1.4fr_auto]">
          <div className="col-span-2 sm:col-span-1">
            <div className="text-[9px] uppercase text-slate-400">Venue</div>
            <div className="text-xs font-semibold">{applicant.venue}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase text-slate-400">Property</div>
            <div className="text-xs font-semibold">{applicant.property}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase text-slate-400">Room</div>
            <div className="text-lg font-bold text-[#347DB5]">
              {applicant.roomNo}
            </div>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-400">Tour</div>
          <div className="text-xs font-medium">{applicant.tour}</div>
          <div className="text-[10px] text-slate-400">{applicant.operator}</div>
        </div>
        <div className="text-center text-[9px] tracking-[0.18em] text-slate-500">
          {applicant.its}-{applicant.roomNo}-{applicant.id}
        </div>
      </div>
    </div>
  );
};

const downloadTags = (applicants: AssignedApplicant[], filename: string) => {
  if (!applicants.length)
    return toast.error(
      "No individual tag details are available for this test batch",
    );
  const escape = (value: string) =>
    value.replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;",
        })[char] || char,
    );
  const tags = applicants
    .map(
      (applicant) =>
        `<section class="tag"><header>FHIMS LUGGAGE TAG</header><main><small>APPLICANT</small><h2>${escape(applicant.name)}</h2><p>ITS ${escape(applicant.its)}</p><div class="grid"><div><small>VENUE</small><b>${escape(applicant.venue)}</b></div><div><small>PROPERTY</small><b>${escape(applicant.property)}</b></div><div><small>ROOM</small><strong>${escape(applicant.roomNo)}</strong></div></div><small>TOUR</small><b>${escape(applicant.tour)}</b><p>${escape(applicant.operator)}</p><footer>${escape(applicant.its)}-${escape(applicant.roomNo)}-${escape(applicant.id)}</footer></main></section>`,
    )
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escape(filename)}</title><style>body{font-family:Arial,sans-serif;margin:20px;display:grid;grid-template-columns:repeat(2,1fr);gap:16px}.tag{border:2px solid #9bd3f5;border-radius:14px;overflow:hidden;break-inside:avoid}.tag header{background:#e3f2fd;color:#347db5;font-size:12px;font-weight:700;padding:10px 14px}.tag main{padding:14px}.tag h2{margin:3px 0;font-size:20px}.tag p{margin:3px 0 10px;color:#64748b;font-size:12px}.tag small{display:block;color:#94a3b8;font-size:9px}.grid{display:grid;grid-template-columns:1fr 1.4fr auto;gap:12px;border-top:1px dashed #cbd5e1;border-bottom:1px dashed #cbd5e1;padding:12px 0;margin:12px 0}.grid b{display:block;font-size:12px}.grid strong{font-size:22px;color:#347db5}.tag footer{text-align:center;font-size:9px;letter-spacing:2px;color:#64748b;margin-top:14px}@media print{body{margin:0}}</style></head><body>${tags}</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${filename}.html`;
  anchor.click();
  URL.revokeObjectURL(url);
  toast.success(
    `${applicants.length} tag${applicants.length === 1 ? "" : "s"} downloaded`,
  );
};

export function LuggageTagAutomationPage() {
  const { setPageHeader } = usePageHeader();
  const [batches, setBatches] = useState(initialBatches);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [tour, setTour] = useState(assignedApplicants[0].tour);
  const [selected, setSelected] = useState<string[]>([]);
  const [individualPreview, setIndividualPreview] =
    useState<AssignedApplicant | null>(null);
  const [batchPreview, setBatchPreview] = useState<TagBatch | null>(null);

  useEffect(() => {
    setPageHeader({
      title: "Luggage Tag Automation",
      icon: <Luggage className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const tours = Array.from(
    new Set(assignedApplicants.map((item) => item.tour)),
  );
  const eligibleApplicants = assignedApplicants.filter(
    (item) => item.tour === tour,
  );
  const filteredBatches = useMemo(
    () =>
      batches.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase()),
      ),
    [batches, search],
  );
  const previewApplicants = eligibleApplicants.filter((item) =>
    selected.includes(item.id),
  );

  const openGenerator = () => {
    const first = assignedApplicants[0];
    setTour(first.tour);
    setSelected([]);
    setCreating(true);
  };

  const changeTour = (value: string) => {
    setTour(value);
    setSelected([]);
  };

  const toggleApplicant = (id: string) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };
  const toggleAll = () => {
    const clearing = selected.length === eligibleApplicants.length;
    setSelected(clearing ? [] : eligibleApplicants.map((item) => item.id));
  };

  const generate = () => {
    if (!selected.length)
      return toast.error("Select at least one assigned applicant");
    const applicants = eligibleApplicants.filter((item) =>
      selected.includes(item.id),
    );
    const properties = Array.from(
      new Set(applicants.map((item) => item.property)),
    );
    const venues = Array.from(new Set(applicants.map((item) => item.venue)));
    const now = new Date();
    const batch: TagBatch = {
      id: `LT-${String(Date.now()).slice(-6)}`,
      tour,
      arrival: Array.from(
        new Set(applicants.map((item) => displayDate(item.arrival))),
      ).join(", "),
      venue: venues.join(", "),
      property: properties.join(", "),
      applicants: applicants.length,
      printed: 0,
      generatedAt:
        now.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " · " +
        now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "Ready",
      applicantIds: applicants.map((item) => item.id),
    };
    setBatches((current) => [batch, ...current]);
    setCreating(false);
    toast.success(
      `${applicants.length} luggage tag${applicants.length === 1 ? "" : "s"} generated`,
    );
  };

  const printBatch = (id: string) => {
    setBatches((current) =>
      current.map((item) =>
        item.id === id
          ? { ...item, printed: item.applicants, status: "Printed" as const }
          : item,
      ),
    );
    toast.success(`Batch ${id} sent to print`);
  };

  const applicantsForBatch = (batch: TagBatch) => {
    if (batch.applicantIds?.length)
      return assignedApplicants.filter((item) =>
        batch.applicantIds?.includes(item.id),
      );
    return assignedApplicants.filter((item) => item.tour === batch.tour);
  };

  return (
    <div className="space-y-6 pb-8">
      {!creating && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by batch, tour, venue or property..."
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            onClick={openGenerator}
            className="bg-[#5B9BD5] text-white hover:bg-blue-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Generate Luggage Tags
          </Button>
        </div>
      )}

      {creating && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-slate-900">
              Generate Luggage Tags
            </div>
            <div className="text-xs text-slate-500">
              Assigned accommodation details are filled automatically for every
              selected applicant.
            </div>
          </div>
          <Button variant="outline" onClick={() => setCreating(false)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to List
          </Button>
        </div>
      )}

      {!creating && (
        <Card className="border-[#5B9BD5]/30 bg-white" style={{ gap: 0 }}>
          <CardContent className="p-0 pb-2">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#E3F2FD]">
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Batch / Tour
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Arrival
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Accommodation
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Tags
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Generated
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Status
                    </TableHead>
                    <TableHead className="px-4 py-3 text-right text-xs text-[#5B9BD5]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBatches.map((batch) => (
                    <TableRow key={batch.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-100">
                            <Tags className="h-4 w-4 text-[#5B9BD5]" />
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-700">
                              {batch.tour}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {batch.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-600">
                        {batch.arrival}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="text-xs font-medium text-gray-700">
                          {batch.property}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {batch.venue}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="text-xs font-medium text-gray-700">
                          {batch.printed}/{batch.applicants} printed
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-600">
                        {batch.generatedAt}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          className={
                            batch.status === "Printed"
                              ? "bg-emerald-50 text-emerald-700"
                              : batch.status === "Ready"
                                ? "bg-sky-50 text-sky-700"
                                : "bg-amber-50 text-amber-700"
                          }
                        >
                          {batch.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="View individual tags"
                          onClick={() => setBatchPreview(batch)}
                        >
                          <Eye className="h-4 w-4 text-[#5B9BD5]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Download tags"
                          onClick={() =>
                            downloadTags(applicantsForBatch(batch), batch.id)
                          }
                        >
                          <Download className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Print batch"
                          onClick={() => printBatch(batch.id)}
                        >
                          <Printer className="h-4 w-4 text-[#5B9BD5]" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filteredBatches.length && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="h-28 text-center text-sm text-gray-500"
                      >
                        No luggage-tag batches found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {creating && (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,.7fr)]">
          <Card className="border-sky-100 bg-white" style={{ gap: 0 }}>
            <CardContent className="p-0">
              <div className="border-b border-sky-100 bg-sky-50/70 px-4 py-3">
                <div className="text-sm font-semibold text-sky-900">
                  Assigned Applicants
                </div>
                <div className="text-[10px] text-slate-500">
                  Only applicants with a confirmed property, room type and room
                  number are eligible.
                </div>
              </div>
              <div className="border-b border-slate-100 p-4">
                {/* <div className="max-w-xl"> */}
                <div className="flex flex-col gap-1">
                  <div className="mb-1 text-xs font-medium text-slate-700">
                    Tour <span className="text-red-500">*</span>
                  </div>
                  <Select value={tour} onValueChange={changeTour}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tours.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
                <div className="text-xs font-medium text-slate-700">
                  {eligibleApplicants.length} eligible applicant
                  {eligibleApplicants.length === 1 ? "" : "s"}
                </div>
                <button
                  type="button"
                  className="text-xs font-medium text-[#5B9BD5]"
                  onClick={toggleAll}
                >
                  {selected.length === eligibleApplicants.length
                    ? "Clear all"
                    : "Select all"}
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {eligibleApplicants.map((applicant) => (
                  <div
                    key={applicant.id}
                    className={`flex items-center gap-3 px-4 py-3 ${selected.includes(applicant.id) ? "bg-sky-50/50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(applicant.id)}
                      onChange={() => toggleApplicant(applicant.id)}
                      className="h-4 w-4 accent-sky-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800">
                        {applicant.name}{" "}
                        <span className="font-normal text-slate-400">
                          · ITS {applicant.its}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {applicant.venue} · {applicant.property} · Room{" "}
                        {applicant.roomNo}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {applicant.operator} · Arrival{" "}
                        {displayDate(applicant.arrival)}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIndividualPreview(applicant);
                      }}
                      title={`View ${applicant.name}'s tag`}
                      aria-label={`View ${applicant.name}'s tag`}
                      className="ml-auto h-8 w-8 shrink-0 border-sky-200 p-0"
                    >
                      <Eye className="h-4 w-4 text-[#5B9BD5]" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 border-t border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-800">
                    {selected.length}
                  </span>{" "}
                  applicant{selected.length === 1 ? "" : "s"} selected
                </div>
                <Button
                  className="w-full bg-[#5B9BD5] text-white hover:bg-blue-600 sm:w-auto"
                  onClick={generate}
                >
                  <Tags className="mr-2 h-4 w-4" />
                  Generate Tags
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card
            className="border-[#5B9BD5]/30 bg-white xl:sticky xl:top-4"
            style={{ gap: 0 }}
          >
            <CardContent className="p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Eye className="h-4 w-4 text-[#5B9BD5]" />
                Tag Preview
              </div>
              {previewApplicants.length ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                  {previewApplicants.map((applicant) => (
                    <TagCard key={applicant.id} applicant={applicant} />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-xs text-slate-400">
                  Select an applicant to preview the tag
                </div>
              )}
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 p-2 text-[10px] text-emerald-700">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Name and accommodation details are populated from the final room
                allocation.
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={Boolean(individualPreview)}
        onOpenChange={(open) => !open && setIndividualPreview(null)}
      >
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Individual Luggage Tag</DialogTitle>
            <DialogDescription>
              Review or download this applicant's generated tag.
            </DialogDescription>
          </DialogHeader>
          {individualPreview && (
            <>
              <TagCard applicant={individualPreview} />
              <div className="flex justify-end">
                <Button
                  className="bg-[#5B9BD5] text-white hover:bg-blue-600"
                  onClick={() =>
                    downloadTags(
                      [individualPreview],
                      `Luggage-Tag-${individualPreview.its}`,
                    )
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Tag
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(batchPreview)}
        onOpenChange={(open) => !open && setBatchPreview(null)}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Individual Tags — {batchPreview?.tour}</DialogTitle>
            <DialogDescription>
              Review every individual tag in this generated batch.
            </DialogDescription>
          </DialogHeader>
          {batchPreview && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {applicantsForBatch(batchPreview).map((applicant) => (
                  <div key={applicant.id}>
                    <TagCard applicant={applicant} />
                    <Button
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={() =>
                        downloadTags(
                          [applicant],
                          `Luggage-Tag-${applicant.its}`,
                        )
                      }
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download {applicant.name}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end border-t pt-3">
                <Button
                  className="bg-[#5B9BD5] text-white hover:bg-blue-600"
                  onClick={() =>
                    downloadTags(
                      applicantsForBatch(batchPreview),
                      batchPreview.id,
                    )
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download All Tags
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
